import { useState, useEffect } from 'react';
import type {
  VisibilityScore,
  VisibilityHistoryResponse,
  VisibilityResponse,
  RegionVisibility,
} from '../types/index.js';
import { apiGet } from '../lib/api.js';
import type { DateRange } from '../components/DateRangeSelector.js';

function getDateRange(range: DateRange): { date_from: string; date_to: string } {
  const to = new Date();
  const from = new Date();
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
  from.setDate(from.getDate() - days);
  return {
    date_from: from.toISOString().slice(0, 10),
    date_to: to.toISOString().slice(0, 10),
  };
}

export interface UseVisibilityResult {
  latest: VisibilityScore[];
  history: VisibilityScore[];
  /**
   * Spec 171: per-region grouped scores from the additive `regions[]` field.
   * Empty when the backend returns no region data (single-region/pre-region
   * brands) — the page then renders the existing flat view unchanged.
   */
  regions: RegionVisibility[];
  loading: boolean;
  error: string | null;
}

export function useVisibility(
  tenantId: string | null,
  brandId: string | null,
  dateRange: DateRange,
): UseVisibilityResult {
  const [latest, setLatest] = useState<VisibilityScore[]>([]);
  const [history, setHistory] = useState<VisibilityScore[]>([]);
  const [regions, setRegions] = useState<RegionVisibility[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !brandId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const { date_from, date_to } = getDateRange(dateRange);
    const base = `/api/v1/tenants/${tenantId}/brands/${brandId}/visibility`;

    Promise.all([
      apiGet<VisibilityResponse>(base),
      apiGet<VisibilityHistoryResponse>(
        `${base}/history?date_from=${date_from}&date_to=${date_to}&granularity=daily`,
      ),
    ])
      .then(([latestRes, historyRes]) => {
        if (cancelled) return;
        setLatest(latestRes.data);
        setHistory(historyRes.data);
        // Additive: only present when the backend has region data. Default to []
        // so single-region/pre-region brands keep the existing flat render.
        setRegions(latestRes.regions ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load visibility data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, brandId, dateRange]);

  return { latest, history, regions, loading, error };
}
